import { InputText } from "primereact/inputtext";
import { FormFieldsPanel } from "../../../../../components/form-fields-panel/form-fields-panel";
import { LabelValue } from "../../../../../components/LabelValue/LabelValue";
import classes from "./JobForm.module.scss";
import { classNames as cx } from "primereact/utils";
import { Dropdown } from "primereact/dropdown";
import { Controller, UseFormReturn } from "react-hook-form";
import { TJobFormData } from "../CreateJobAndSchedule.model";
import { InputNumber } from "primereact/inputnumber";
import { getFileNameFromPath } from "../../../../Employee/EmployeeForm/helpers";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useState } from "react";
import { If } from "../../../../../components/If/If";
import { AttachMultipleDocsPopup } from "../../../../../components/AttachMultipleDocsPopup/AttachMultipleDocsPopup";
import { Button } from "primereact/button";
import { CustomerCategoryEnum } from "../../../SalesLead/Lead.model";

type JobFormProps = {
  form: UseFormReturn<TJobFormData, any>;
  /** Editing an existing series rather than creating a job. */
  isSeriesEdit?: boolean;
};

export const JobForm = ({ form, isSeriesEdit }: JobFormProps) => {
  const [openMultipleDocsPopup, setOpenMultipleDocsPopup] = useState(false);
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;
  const [
    attachment1,
    attachment2Path,
    leadAdditionalNotes,
    customerCategory,
    sowFrequencies,
  ] = watch([
    "attachment1",
    "attachment2Path",
    "leadAdditionalNotes",
    "customerCategory",
    "sowFrequencies",
  ]);

  const getFormErrorMessage = (name: any) => {
    return (
      // ✅ THIS IS OK
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const handleAddDocuments = (files: File[]) => {
    setValue(
      "attachment2Path",
      attachment2Path ? [...attachment2Path, ...files] : [...files]
    );
  };

  const formatFrequency = () => {
    return (sowFrequencies || []).map((item) => {
      const formattedFrequency = item.frequency.toLowerCase().replace("_", " ");
      return {
        ...item,
        frequency:
          formattedFrequency.charAt(0).toUpperCase() +
          formattedFrequency.slice(1),
      };
    });
  };

  return (
    <div className={classes.content}>
      <div className={classes.section}>
        <FormFieldsPanel
          label="Basic Setup"
          className={cx(classes.fieldsPanel)}
        >
          <div className={classes.gridTemplate}>
            <LabelValue label="Client N">
              <Controller
                name="clientId"
                render={({ field }) => <InputText {...field} disabled />}
                control={control}
              />
            </LabelValue>
            <LabelValue label="Job name" isRequired>
              <Controller
                name="name"
                render={({ field }) => (
                  <InputText
                    {...field}
                    className={cx({ "p-invalid": errors.name })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("name")}
            </LabelValue>
            <If condition={customerCategory === CustomerCategoryEnum.CORPORATE}>
              <LabelValue label="Company Name">
                <Controller
                  name="clientCompanyName"
                  render={({ field }) => {
                    return <InputText {...field} disabled />;
                  }}
                  control={control}
                />
              </LabelValue>
              <LabelValue label="First Name">
                <Controller
                  name="clientFirstName"
                  render={({ field }) => <InputText {...field} disabled />}
                  control={control}
                />
              </LabelValue>
            </If>
            <LabelValue label="Contact Email">
              <Controller
                name="cientContactEmail"
                render={({ field }) => <InputText {...field} disabled />}
                control={control}
              />
            </LabelValue>
            <LabelValue label="Work Phone/Other Phone">
              <Controller
                name="clientPhoneNumber"
                render={({ field }) => <InputText {...field} disabled />}
                control={control}
              />
            </LabelValue>
            <If condition={customerCategory === CustomerCategoryEnum.CORPORATE}>
              <LabelValue label="Contact Name">
                <Controller
                  name="clientContactName"
                  render={({ field }) => <InputText {...field} disabled />}
                  control={control}
                />
              </LabelValue>
              <LabelValue label="Last Name">
                <Controller
                  name="clientLastName"
                  render={({ field }) => <InputText {...field} disabled />}
                  control={control}
                />
              </LabelValue>
            </If>
          </div>
        </FormFieldsPanel>
      </div>
      <div className={classes.section}>
        <FormFieldsPanel label="Advanced" className={classes.fieldsPanel}>
          <LabelValue label="Weekly Budget Labour Hours">
            <Controller
              name="leadlaborGoalHours"
              render={({ field }) => (
                <InputNumber
                  value={field.value}
                  onValueChange={({ value }) => field.onChange(value ?? null)}
                  min={0}
                  maxFractionDigits={2}
                  placeholder="Weekly Budget Labour Hours"
                  aria-label="Weekly Budget Labour Hours"
                />
              )}
              control={control}
            />
          </LabelValue>
        </FormFieldsPanel>
        <FormFieldsPanel label="Location" className={classes.fieldsPanel}>
          <LabelValue label="Address">
            <Controller
              name="leadAddress"
              render={({ field }) => <InputText {...field} disabled />}
              control={control}
            />
          </LabelValue>
        </FormFieldsPanel>
      </div>
      <div className={classes.section}>
        <FormFieldsPanel label="Notes" className={classes.fieldsPanel}>
          <div className={classes.gridTemplateNotes}>
            <LabelValue label="Invoice Generation">
              <Controller
                name="invoiceGeneration"
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    appendTo="self"
                    options={[
                      { label: "Daily", value: "DAILY" },
                      { label: "Weekly", value: "WEEKLY" },
                      { label: "Monthly", value: "MONTHLY" },
                    ]}
                    style={{ width: "240px" }}
                  />
                )}
                control={control}
              />
            </LabelValue>
            <div />
            <LabelValue label="Job Type">
              <Controller
                name="jobType"
                render={({ field }) => <InputText {...field} disabled />}
                control={control}
              />
            </LabelValue>
            <div />
            <LabelValue label="Attachment 1">
              <a
                href={`${process.env.REACT_APP_DSC_SERVICE_DOMAIN}/${attachment1}`}
                download
                target="_blank"
                rel="noreferrer"
              >
                {attachment1 && getFileNameFromPath(attachment1)}
              </a>
            </LabelValue>
          </div>

          <div className={classes.otherDocsContainer}>
            <div className={classes.otherDocsTitle}>
              <span>Attachment 2</span>
              {/*
                Create only. This save uploads the chosen files and sends their
                paths with the new job; the series update has no upload step and
                no attachment field, so on the edit route the button took files
                and dropped them. An existing job's attachments are added from a
                shift, which is also where this screen's other per-occurrence
                fields now point.
              */}
              {isSeriesEdit ? (
                <small className={classes.perOccurrence}>
                  Added per occurrence &mdash; open a shift on the calendar to
                  attach files.
                </small>
              ) : (
                <Button
                  label="Add files"
                  icon="pi pi-plus"
                  type="button"
                  className="p-button-text"
                  onClick={() => setOpenMultipleDocsPopup(true)}
                />
              )}
            </div>
            <div className={classes.docList}>
              <DataTable value={attachment2Path || []}>
                <Column
                  header="Name"
                  body={(rowData) => <span>{rowData.name}</span>}
                ></Column>
                <Column
                  style={{ width: "60px" }}
                  header="Actions"
                  body={(rowData) => (
                    <Button
                      icon="pi pi-times"
                      className=" p-button-sm p-button-rounded p-button-danger p-button-text"
                      aria-label="Cancel"
                      type="button"
                      onClick={() => {
                        const docsList = attachment2Path.filter(
                          (_doc) => _doc.name !== rowData.name
                        );
                        setValue("attachment2Path", docsList);
                      }}
                    />
                  )}
                ></Column>
              </DataTable>
            </div>
          </div>
          <div className={classes.additionalNotes}>
            <h4>Additional Notes</h4>
            {leadAdditionalNotes.map((note) => (
              <div key={note.category}>
                {note.category !== "Scope of works" && (
                  <>
                    <h5>{note.category}</h5>
                    {note.names.map((n, i) => (
                      <ul key={i}>
                        <li>{n}</li>
                      </ul>
                    ))}
                  </>
                )}
              </div>
            ))}
          </div>
          <div className={classes.sowFrequencies}>
            <DataTable
              value={formatFrequency()}
              showGridlines
              tableStyle={{ minWidth: "35rem" }}
            >
              <Column
                field="name"
                header="Scope of work"
                style={{ width: "10%" }}
              ></Column>
              <Column
                field="frequency"
                header="Frequency"
                style={{ width: "5%" }}
              ></Column>
            </DataTable>
          </div>
          <If condition={openMultipleDocsPopup}>
            <AttachMultipleDocsPopup
              onValueSet={handleAddDocuments}
              onHide={() => setOpenMultipleDocsPopup(false)}
            />
          </If>
        </FormFieldsPanel>
      </div>
    </div>
  );
};
